// Library Methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CautionsList from "../SynoptophoreGraph/shared/CautionsList";
import SummaryCard from "../SynoptophoreGraph/shared/SummaryCard";
import { FigureRow, Figures } from "../SynoptophoreGraph/shared/FigureRow";
import MeridianCard from "./MeridianCard";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Types
import { AniseikoniaMeridianKeyType } from "src/services/exams/types";

// Utils
import {
  fraction,
  num,
  pct,
  signed,
} from "../SynoptophoreGraph/shared/formatters";

const MERIDIANS: Array<{
  key: AniseikoniaMeridianKeyType;
  label: string;
}> = [
  { key: "horizontal", label: "Horizontal" },
  { key: "vertical", label: "Vertical" },
  { key: "overall", label: "Overall" },
];

const AniseikoniaGraph = () => {
  const { t } = useTranslation();
  const { examReport } = useContext(ReportSectionContext);

  const summary = examReport?.postProcessing?.summary;
  const directComparison = summary?.DirectComparison;
  const module = examReport?.postProcessing?.modules?.DirectComparison;
  const instrument = examReport?.postProcessing?.instrument;
  const calibration = examReport?.postProcessing?.calibration;
  const geometry = examReport?.exam?.targetGeometry;

  const probes = module?.reliability?.probes;

  const meridiansToShow = useMemo(
    () =>
      MERIDIANS.filter(
        (meridian) => directComparison?.[meridian.key] !== undefined
      ),
    [directComparison]
  );

  if (!directComparison) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("No Data Available.")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" width="100%" gap="16px">
      {/* Reasons not to act on the numbers go first. */}
      <Box display="flex" flexWrap="wrap" gap="16px">
        <Box display="flex" flexDirection="column" gap="4px" sx={{ flex: 1 }}>
          <Typography
            fontSize="12px"
            color="#5B6D7A"
            sx={{ textTransform: "uppercase" }}
          >
            {t("Section Cautions")}
          </Typography>
          <CautionsList cautions={summary?.cautions} />
        </Box>
      </Box>

      {/* The headline. `overallLargerEye` is carried so nothing has to derive which eye saw the
          larger image from the sign of the percentage. */}
      <SummaryCard title={t("Aniseikonia")} cautions={null}>
        <Box sx={{ pb: "8px" }}>
          <Figures>
            <FigureRow
              label={t("Image Size Difference")}
              value={
                <Typography fontSize="34px" fontWeight={700}>
                  {signed(summary?.overallPercent, 1)}%
                </Typography>
              }
              sub={
                summary?.overallSignificant
                  ? t("Above Detection Floor")
                  : t("Below Detection Floor")
              }
            />
            <FigureRow
              label={t("Larger Image")}
              value={
                summary?.overallLargerEye
                  ? t(summary.overallLargerEye)
                  : t("N/A")
              }
            />
            <FigureRow
              label={t("Meridional Difference")}
              value={pct(summary?.meridionalDifferencePercent, 1)}
            />
          </Figures>
        </Box>
      </SummaryCard>

      {meridiansToShow.map((meridian) => (
        <MeridianCard
          key={meridian.key}
          meridian={meridian.key}
          title={t(meridian.label)}
          summary={directComparison?.[meridian.key]}
          result={module?.meridians?.[meridian.key]}
          geometry={geometry}
        />
      ))}

      {/* Two of every six presentations are control probes with a known right answer. Their
          individual kinds are deliberately not shown -- a probe the technician can pick out is
          not a control any more -- so only the aggregate appears here. */}
      <SummaryCard title={t("Control Probes")} cautions={null}>
        <Box sx={{ pb: "8px" }}>
          <Figures>
            <FigureRow
              label={t("Pass Rate")}
              value={fraction(module?.reliability?.controlProbePassRate, 0)}
            />
            <FigureRow
              label={t("Probes Passed")}
              value={
                probes ? `${probes.correct} / ${probes.presented}` : t("N/A")
              }
            />
            <FigureRow
              label={t("Mean Error")}
              value={pct(probes?.meanError, 2)}
            />
            <FigureRow
              label={t("Zero Probe Bias")}
              value={pct(module?.reliability?.zeroProbeBiasPercent, 2)}
            />
            <FigureRow
              label={t("Fusion Loss Events")}
              value={module?.reliability?.fusionLossEvents ?? 0}
            />
            <FigureRow
              label={t("Suppression Events")}
              value={module?.reliability?.suppressionEvents ?? 0}
            />
          </Figures>
        </Box>
      </SummaryCard>

      {/* A per-eye difference in angular magnification caused by where the pupils sat is, at the
          level of the patient's report, the same thing as the patient's own aniseikonia --
          nothing downstream can separate them. That is why this is shown and not hidden behind a
          detail panel, and why the fit is called out when it falls outside the range the optics
          were characterised over. */}
      <SummaryCard title={t("Instrument Fit")} cautions={null}>
        <Box sx={{ pb: "8px" }}>
          {instrument?.withinCharacterisedFitRange === false && (
            <Typography fontSize="12px" color="#E10C0C" sx={{ mb: "8px" }}>
              {t("Outside Characterised Range")}
            </Typography>
          )}
          <Figures>
            <FigureRow
              label={t("Interpupillary Distance")}
              value={num(
                instrument?.interpupillaryDistanceMetres
                  ? instrument.interpupillaryDistanceMetres * 1000
                  : null,
                1,
                " mm"
              )}
              sub={
                instrument?.interpupillaryDistanceMeasured
                  ? t("Measured IPD")
                  : t("Assumed IPD")
              }
            />
            <FigureRow
              label={t("Off Axis (Left)")}
              value={num(
                instrument?.leftOffAxisMetres != null
                  ? instrument.leftOffAxisMetres * 1000
                  : null,
                1,
                " mm"
              )}
            />
            <FigureRow
              label={t("Off Axis (Right)")}
              value={num(
                instrument?.rightOffAxisMetres != null
                  ? instrument.rightOffAxisMetres * 1000
                  : null,
                1,
                " mm"
              )}
            />
            <FigureRow
              label={t("Within Characterised Range")}
              value={
                instrument?.withinCharacterisedFitRange ? t("Yes") : t("No")
              }
            />
            <FigureRow
              label={t("Calibration Residual")}
              value={num(calibration?.residualDegrees, 1, "°")}
            />
            <FigureRow
              label={t("Quality Floor Met")}
              value={calibration?.qualityFloorMet ? t("Yes") : t("No")}
            />
          </Figures>
        </Box>
      </SummaryCard>
    </Box>
  );
};

export default AniseikoniaGraph;
